.topbar {
	display: grid;
	background: var(--cf-topbar-background);
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	/* Every page composing this widget lays it out as a CSS Grid item
	   alongside a content area that comes later in DOM order (`.topbar`
	   then the content below it) -- with both left at the default `z-index:
	   auto`, the later element paints on top for any overlapping region.
	   The tooltip pseudo-elements this widget's buttons use (`.tooltip:
	   before` in `index.css`, `z-index: 100`) only control stacking *within*
	   this element's own local stacking context; without `.topbar` itself
	   establishing one that outranks the content area's, a tooltip that
	   overflows downward (`.tooltip.down`) gets painted under the content
	   below instead of over it. `position: relative` is what makes the
	   `z-index` here actually apply -- a plain grid item's z-index without a
	   positioning context is unreliable across browsers. */
	position: relative;
	z-index: 999;
	/* A fixed (not per-instance) name is deliberate: exactly one `Topbar` is
	   ever mounted at a time on any page that uses it, so there's no
	   duplicate-name collision risk (unlike the per-issue cover morph, which
	   needs a unique name per element). This is what makes the navbar read
	   as one continuous element across a router-driven View Transition
	   (`<Link viewTransition>` / `navigate({ viewTransition: true })`)
	   instead of the whole page -- navbar included -- hard-cutting or
	   cross-fading as an undifferentiated block. */
	view-transition-name: topbar-nav;
}

.left {
	display: flex;
	align-items: center;
	/* Matches `.right`'s own `padding-inline-end` exactly -- every page used to
	   bring its own copy of this, and one (`templates/morningstar/home`)
	   never had a left-side one at all, which is why its back-button-side
	   icons sat flush against the edge while the right-side ones had a
	   20px margin. One declaration here replaces every per-page copy. */
	padding-inline-start: 20px;
	/* `.topbar`'s `1fr auto 1fr` only actually gives `.left`/`.right` equal
	   width if both tracks are allowed to shrink below their own content's
	   natural size -- a bare `1fr` grid track's *minimum* is `auto` (its
	   own min-content width) by default, not 0. Without this, whichever
	   side has more content (an extra icon here, home's `InstallBtn`
	   there) force-grows its own track past the other one, pushing the
	   centered logo off-center or overlapping it outright (user-reported
	   2026-08-27: home's Install button sitting behind the logo; the
	   issue reader's sidebar-toggle icon shifting the logo sideways). */
	min-width: 0;
}

.center {
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

/* The single source of truth for logo sizing -- every page used to bring
   its own `.logo img`/`.title img` rule, and the issue-reader family
   (article/responsive/single-view/double-view) had drifted to a shorter
   `calc(var(--top-bar-height) - 10px)` while every other page (preview,
   drafts, favorite, home) used `var(--top-bar-height)` directly, so the
   logo visibly changed size crossing between them despite both using this
   same widget. One rule here, matching the majority value, replaces all of
   them (see the per-page CSS files this superseded for the removed
   duplicates). */
.center img {
	max-width: 100%;
	/* The `topbar.logo.max-height` token (`topbar.theme.ts`): one size for
	   every route that mounts this widget (home, sections, preview, the issue
	   reader), adjusted per template through its fragment's `defaults`
	   rather than by each page. Pages used to set it themselves --
	   starmagazine's, daily's and toyota's homes 10px under the bar,
	   everything else 1px -- which is how the reader came to draw a larger
	   logo than those homes. The fallback is the token's own `md` default
	   (the bar less its 1px `border-bottom`).

	   A length rather than `max-height: 100%`: the image is not a direct
	   child of `.center` (it is wrapped in a `.title`/`.logo` div and again
	   in `NavbarIconLogo`'s `<a>`/`<Link>`), so a percentage would resolve
	   through auto-height wrappers to `none` and the image would fall back
	   to its intrinsic size. */
	max-height: var(--cf-topbar-logo-max-height, calc(var(--top-bar-height) - 1px));
}

/* `max-height` (above), not `max-width`, is what actually constrains this
   image's rendered size day to day -- `.center` is the grid's `auto`
   column, sized to fit the image itself, so a percentage `max-width`
   against it never actually engages. Shrinking `max-height` by 30% on
   mobile scales both rendered dimensions down together (the image keeps
   its own aspect ratio), which is what "make the logo 30% smaller" means
   visually regardless of tenant/logo aspect ratio -- user-directed
   2026-08-27, one of two contributing causes of the Install button
   sitting behind the logo on home (the other, `.left`/`.right` missing
   `min-width: 0`, is fixed above). Applies to every topbar via this one
   shared rule, not just home's. */
@media screen and (max-width: 767px) {
	.center img {
		max-height: var(--cf-topbar-logo-max-height, calc(var(--top-bar-height) * 0.7));
	}
}

/* Matches the issue reader's own mobile rule (`widgets/issue-reader/
   responsive/responsive.module.css`'s `.topbar .logo img`), the reference
   this was compared against -- user-reported 2026-09-02: poets' home,
   favorite, and feed pages each carried their own divergent copy of this
   (`width: 100%` at a plain `max-width: 700px`, no `hover`/`pointer`
   condition), so the logo visibly changed size crossing between the reader
   and every other poets screen, and again between 480-700px depending on
   input type. One rule here, matching the reader's exact condition and
   values, replaces all three per-page copies. */
@media only screen and (hover: none) and (pointer: coarse) and (max-width: 480px) {
	.center img {
		width: 100%;
		height: auto;
	}
}

.right {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding-inline-end: 20px;
	/* Same reasoning as `.left`'s own `min-width: 0` above. */
	min-width: 0;
}

/* Canonical icon-button sizing -- the single source of truth every page
   used to bring its own (often conflicting) copy of: some at 32x32
   (article, preview-basic, responsive, single-view, double-view), some at
   24x24 or entirely unstyled (preview, drafts, favorite, home), none of
   them centered/margined identically. A page's own more specific selector
   (e.g. a pill-shaped text button like preview's "Log Out" or home's
   Collections/Issues buttons) still wins over this on real CSS specificity
   grounds -- this only sets the default every plain icon button falls back
   to. */
.left button,
.right button {
	cursor: pointer;
	width: 32px;
	height: 32px;
	margin: 0 15px;
	border: none;
	padding: 0;
	background: none;
	opacity: 1;
	display: flex;
	justify-content: center;
	align-items: center;
	/* iOS Safari keeps a `<button>`'s native form-control line-height/
	   baseline metrics even once `border`/`padding` are zeroed out, which
	   can throw off `align-items: center`'s vertical centering of a single
	   icon child (Chrome doesn't have this quirk, so this was invisible in
	   every earlier browser-based check this session -- user-reported on a
	   real iPhone, 2026-08-27). `appearance: none` opts the button out of
	   native control rendering entirely; `line-height: 0` removes the same
	   metrics' influence for any engine that still accounts for them after
	   that. */
	-webkit-appearance: none;
	appearance: none;
	line-height: 0;
}

.left button:disabled,
.right button:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* The container already provides 20px of edge spacing via `padding-left`/
   `padding-right` above -- without zeroing the outermost button's own
   margin on that side, it would stack with the container's padding into a
   35px gap instead of the intended 20px. */
.left button:first-of-type {
	margin-inline-start: 0;
	border-radius: 8px;
}

.right button:last-of-type {
	margin-inline-end: 0;
	border-radius: 8px;
}

.left button:hover > div,
.right button:hover > div {
	background-color: var(--app-accent-color) !important;
}

.left button > div,
.right button > div {
	transition: background-color ease-out 220ms;
}

/* Single source of truth for every navbar icon's base/hover stroke color --
   this exact selector pair (`nav.topbar svg` / `nav.topbar button:hover
   svg`) used to be duplicated verbatim in six separate per-page/per-widget
   stylesheets (`pages/article`, `pages/drafts`, `widgets/issue-reader/
   responsive`, `widgets/issue-reader/replica` and its `single`/`double`
   views), all consuming the same `--topbar-buttons-stroke`/
   `-stroke-hover` pair (defined once per template, e.g.
   `templates/morningstar/app.css`) but only reaching whichever pages
   happened to carry a copy -- `pages/preview`/`pages/preview-basic`/
   `pages/favorite`/morningstar `home` never had one, so their icons'
   per-instance `color` prop was the only thing setting their color, out of
   sync with everyone else. A real CSS rule (this one) always wins over an
   SVG `color`/`stroke` presentation attribute regardless of selector
   specificity, so every per-icon `color` prop across the app was updated
   to reference this same `var(--topbar-buttons-stroke)` too -- redundant
   for anything inside `.topbar` (this rule already wins), authoritative
   for the one icon set that renders outside it (morningstar home's bottom
   mobile tab bar reuses the same `ButtonBar` component standalone). */
.topbar svg {
	stroke: var(--topbar-buttons-stroke);
}

.topbar button:hover svg {
	stroke: var(--topbar-buttons-stroke-hover);
}
